import {computed, ComputedRef, Ref} from "vue";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import SearchingAndSuggestionUtil, {SearchForParams} from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import {GridApi, ICellEditorParams, ICellRendererParams} from "ag-grid-community";
import MetaData from "@/utils/common/MetaData.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import AbsAGGridEditViewColumn from "@/utils/UIConstants/AGGridEditViewColumns/AbsAGGridEditViewColumn.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import Translator from "@/utils/common/Translator.ts";
import {FromGridAction} from "@/utils/UIConstants/enums.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import EntityReferenceData = namaTypes.EntityReferenceData;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import GenericValue = namaTypes.GenericValue;

export default class ReferenceGridFieldWidgetUtil extends FieldWidgetUtil {
  gridId: string;
  handler: EditScreenGridHandler
  referencedEntityType: ComputedRef<string>;
  row: number;
  fieldValue: Ref<EntityReferenceData | null | undefined>;
  params?: ICellEditorParams | ICellRendererParams;

  constructor(handler: EditScreenGridHandler, columnMetadata: FieldMetaData, fieldValue: Ref<EntityReferenceData | null | undefined>, row: number) {
    super(handler.presenter, columnMetadata, true);
    this.fieldValue = fieldValue;
    this.row = row;
    if (columnMetadata.fieldType === FieldType.Genericreference)
      this.referencedEntityType = computed(() => this.fetchFieldValue() && this.fetchFieldValue().entityType ? this.fetchFieldValue().entityType : this.calcDefaultEntityType() ?? "");
    else
      this.referencedEntityType = computed(() => columnMetadata.referencedEntityType ?? "");
    this.handler = handler;
    this.gridId = handler.gridId;
  }

  private calcDefaultEntityType() {
    if (!this.params)
      return;
    return this.params['referencedEntityType'];
  }

  override saveChanges = async (forceUpdate: boolean = false) => {
    let originalFieldValue = this.fieldValue.value || {};
    let cellTextValue = this.inputFieldValueRef.value;
    if (!this.params)
      return;
    if (SearchingAndSuggestionUtil.createOptionText(originalFieldValue) !== cellTextValue || (cellTextValue && !originalFieldValue) || forceUpdate) {
      await this.onUserInputFinished();
      if (originalFieldValue.id === this.fieldValue.value?.id && !forceUpdate)
        return;
      const rowNode = this.params.api.getRowNode(this.params.data.id);
      if (rowNode)
        rowNode.setDataValue(this.params?.column!.getColId(), this.fieldValue.value);
    }
  }

  override formattedValue(): ComputedRef<any> {
    return computed(() => {
      let value = this.fetchFieldValue();
      if (ObjectChecker.isEmptyOrNull(value))
        return '';
      const optionText = SearchingAndSuggestionUtil.createOptionText(value);
      if (value && optionText)
        return optionText;
      return null;
    });
  }

  override fetchFieldValue(): any {
    return this.fieldValue.value;
  }

  onSelect = async (value?: EntityReferenceData, ignoreFieldsToUpdate?: boolean) => {
    this.onPartialUserInput(value ? value.code : value);
    let originalValue = this.fetchFieldValue() as EntityReferenceData | null | undefined;
    this.fieldValue.value = value;
    if (!value && this.fieldMetaData.fieldType === FieldType.Genericreference && originalValue && originalValue.entityType)
      this.fieldValue.value = {entityType: originalValue.entityType};
    const loadingStore = useLoadingStore();
    if (value && value['fieldsToUpdate'] && !ignoreFieldsToUpdate) {
      loadingStore.startLoading();
      await this.presenter.fetchDispatcher().onGuiActionUpdateList(value['fieldsToUpdate'] as namaTypes.ListOfGenericValues, this.row);
      loadingStore.stopLoading();
    }
  }

  override onUserInputFinished = async () => {
    let inputFieldValue = this.inputFieldValueRef.value;
    let originalValue = this.fetchFieldValue() as EntityReferenceData | null | undefined;
    if ((originalValue && inputFieldValue && inputFieldValue === originalValue.code) || !this.referencedEntityType.value)
      return;
    let searchContextInfo: FieldActionContextInfo = this.presenter.createSearchContextInfo(this.fieldId(), this.row);
    let matchedRecord = await EditScreenFieldUtils.findRefByCode(this.referencedEntityType.value, inputFieldValue, searchContextInfo, this);
    await this.onSelect(matchedRecord, true);
  }

  async searchFor(p: SearchForParams) {
    p.row = p.row ?? -1;
    if (!this.isSuggestionProviderField)
      return await SearchingAndSuggestionUtil.searchFor(p);
    let placeExtraFilter;
    let contextFields: Record<string, GenericValue> = {};
    if (this.presenter.isEditPresenter()) {
      placeExtraFilter = (this.presenter as EditPresenter).getPlaceExtraFieldFilterId();
      this.presenter.fetchDispatcher().addContextFieldsToMap(this.fieldId(), this.createContextFieldsIds(), p.row
        , contextFields, p.row !== -1 ? FromGridAction.Yes : FromGridAction.No, [], {});
    }
    let newVar = await SearchingAndSuggestionUtil.suggest({
      placeExtraFilter,
      fieldId: this.fieldId(),
      entityType: this.presenter.entityType,
      contextFields,
      currentText: p.query,
      rowIndex: p.row
    });
    let list = newVar.map(l => {
      if (l.vueValue) {
        if (l.fieldsToUpdate)
          l.vueValue['fieldsToUpdate'] = l.fieldsToUpdate;
        if (l.text)
          l.vueValue['optionCaption'] = Translator.getNameForFields(l.text);
      }
      return l.vueValue
    }) as EntityReferenceData[];
    SearchingAndSuggestionUtil.enhanceListForDisplaying(list, p);
    return list;
  }

  onInsertAtEndByOrderClicked = async (values: EntityReferenceData[]) => {
    if (!values || !values.length)
      return;
    let sortedValues = values.sort(
      (o1, o2) =>
        o1[ListViewUtils.REPEAT_COUNT_COLUMN_ID] - o2[ListViewUtils.REPEAT_COUNT_COLUMN_ID]
    );
    await this.appendRowsAtEnd(sortedValues, false);
    let api = this.params!.api;
    this.refreshGridData(api);
  }

  private async appendRowsAtEnd(sortedValues: namaTypes.EntityReferenceData[], considerRepeatCount: boolean = true) {
    let editPresenter: EditPresenter | null = null;
    if (this.presenter instanceof EditPresenter) {
      editPresenter = this.presenter as EditPresenter;
    }
    try {
      editPresenter?.beginBulkUpdate();
      const record = this.presenter.fetchRecord();
      let gridRowData = NamaCommon.getFieldValue(record, this.gridId);
      let rowDataLength = gridRowData.length;
      let api = this.params!.api;
      const canAppendRow = this.canAppendRow();
      for (let value of sortedValues) {
        let repeatCount = value[ListViewUtils.REPEAT_COUNT_COLUMN_ID] && considerRepeatCount ? value[ListViewUtils.REPEAT_COUNT_COLUMN_ID] : 1;
        for (let i = 0; i < repeatCount; i++) {
          let targetRowIndex = rowDataLength + i;
          if (!canAppendRow)
            continue;
          await this.appendNewRow(targetRowIndex);
          NamaCommon.setFieldValue(record, this.fieldId(), value, targetRowIndex);
          AGGridUtils.syncAgGridRows(this.presenter, this.gridId, api);
          await AbsAGGridEditViewColumn.callPostActions(this.handler.dispatcher, this.fieldId(), targetRowIndex, api);
          AGGridUtils.syncAgGridRows(this.presenter, this.gridId, api);
        }
        rowDataLength += repeatCount;
      }
    } finally {
      editPresenter?.endBulkUpdate();
    }
  }

  onInsertAtEndClicked = async (values: EntityReferenceData[]) => {
    if (!values || !values.length)
      return;
    await this.appendRowsAtEnd(values);
    let api = this.params!.api;
    this.refreshGridData(api);
  }

  private refreshGridData(api: GridApi) {
    const gridRowData = NamaCommon.getFieldValue(this.presenter.fetchRecord(), this.gridId);
    api.setGridOption("rowData", gridRowData);
  }

  onSelectMultiple = async (values: EntityReferenceData[]) => {
    if (!values || !values.length)
      return;
    const firstSelectedRow = values[0];
    if (values.length === 1) {
      await this.onSelect(firstSelectedRow);
      await this.saveChanges(true);
      return;
    }
    let editPresenter: EditPresenter | null = null;
    if (this.presenter instanceof EditPresenter) {
      editPresenter = this.presenter as EditPresenter;
    }
    editPresenter?.beginBulkUpdate();
    let api = this.params!.api;
    try {
      const record = this.presenter.fetchRecord();
      let gridRowData = NamaCommon.getFieldValue(record, this.gridId);
      NamaCommon.setFieldValue(record, this.fieldId(), firstSelectedRow, this.row);
      await AbsAGGridEditViewColumn.callPostActions(this.handler.dispatcher, this.fieldId(), this.row, api);
      AGGridUtils.syncAgGridRows(this.presenter, this.gridId, api)
      if (firstSelectedRow[ListViewUtils.REPEAT_COUNT_COLUMN_ID] > 1)
        firstSelectedRow[ListViewUtils.REPEAT_COUNT_COLUMN_ID]--;
      let currentRowIndex = this.row;
      for (let j = 0; j < values.length; j++) {
        let value = values[j];
        let repeatCount = value[ListViewUtils.REPEAT_COUNT_COLUMN_ID] > 1 ? value[ListViewUtils.REPEAT_COUNT_COLUMN_ID] : 1;
        if (j === 0 && value[ListViewUtils.REPEAT_COUNT_COLUMN_ID] === 0)
          continue;
        for (let i = 1; i <= repeatCount; i++) {
          let targetRowIndex = currentRowIndex + i;
          let targetRowData = gridRowData[targetRowIndex];
          if (!targetRowData) {
            if (!this.canAppendRow())
              break;
            await this.appendNewRow(targetRowData);
            AGGridUtils.syncAgGridRows(this.presenter, this.gridId, api)
          }
          NamaCommon.setFieldValue(record, this.fieldId(), value, targetRowIndex);
          AGGridUtils.syncAgGridRows(this.presenter, this.gridId, api)
          await AbsAGGridEditViewColumn.callPostActions(this.handler.dispatcher, this.fieldId(), targetRowIndex, api);
          AGGridUtils.syncAgGridRows(this.presenter, this.gridId, api)
        }
        currentRowIndex += repeatCount;
      }
    } finally {
      editPresenter?.endBulkUpdate();
      this.refreshGridData(api)
    }
  }

  private async appendNewRow(targetRowIndex: number) {
    const copy = {};
    const id = await ClientCache.asyncNextGUIDId();
    copy[CommonFieldIds.ID] = id;
    copy["g2Id"] = id;
    let rows = NamaCommon.getFieldValue(this.presenter.fetchRecord(), this.gridId);
    rows.push(copy);
    await this.presenter.fetchDispatcher()?.afterRowInserted(this.handler, targetRowIndex, false);
  }

  canAppendRow() {
    if (this.presenter.isFieldDisabled(this.gridId))
      return false;
    let gridMetadata = MetaData.findFieldMetadata(this.gridId, this.presenter.fetchMetadata());
    if (gridMetadata && (gridMetadata.systemField || gridMetadata.guiField))
      return false;
    if (this.presenter.fetchDispatcher()?.isAppendRowDisabled(this.gridId))
      return false;
    return !this.presenter.fetchDispatcher()?.isViewOnlyMode();
  }
}
